import { Link } from 'react-router-dom';
import { useT } from '@/i18n';
import type { Child, ConditionKey } from '@/api/types';
import { BandPill, Button, Check, Heart, Illustration, Lock, Pencil, Users } from '@/components/ui';
import { cx } from '@/components/ui/cx';
import { photo } from '@/lib/photos';
import { learnPicks } from '@/content/learn';
import { COMPANY } from '@/content/company';

/** The big friendly start for a parent with nothing on the home screen yet. */
export const WelcomeHero = ({ title, body, cta, ctaTo }: { title: string; body: string; cta: string; ctaTo: string }) => (
  <section aria-labelledby="welcome" className="grid items-center overflow-hidden rounded-xl bg-white shadow-1 md:grid-cols-[1.2fr_1fr]">
    <div className="p-7 md:p-9">
      <h2 id="welcome" className="text-h1">{title}</h2>
      <p className="mt-2 max-w-[32em] text-[17px] leading-relaxed text-ink-soft">{body}</p>
      <Button as="a" href={ctaTo} size="lg" className="mt-6">
        {cta}
      </Button>
    </div>
    <div aria-hidden="true" className="hidden h-full min-h-[260px] md:block">
      <Illustration src={photo('home-welcome')} alt="" position="50% 45%" eager />
    </div>
  </section>
);

const STEPS = ['child', 'checkin', 'summary', 'report'] as const;

/** Where the parent is: add child → home assessment → free summary → full report and plan. */
export const JourneySteps = ({ step }: { step: 1 | 2 | 3 | 4 }) => {
  const t = useT();
  return (
    <nav aria-label={t('journey.label')} className="rounded-xl bg-white/70 p-5 ring-1 ring-line">
      <ol className="grid gap-3 sm:grid-cols-4">
        {STEPS.map((s, i) => {
          const n = i + 1;
          const state = n < step ? 'done' : n === step ? 'now' : 'next';
          return (
            <li key={s} aria-current={state === 'now' ? 'step' : undefined} className="flex items-center gap-3 sm:flex-col sm:items-start">
              <span
                className={cx(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-display text-[16px] font-extrabold',
                  state === 'done' && 'bg-progress text-white',
                  state === 'now' && 'bg-lagoon-600 text-white shadow-lagoon',
                  state === 'next' && 'bg-paper text-ink-muted ring-1 ring-line',
                )}
              >
                {state === 'done' ? <Check className="h-5 w-5" /> : n}
              </span>
              <span>
                <span className={cx('block text-label', state === 'next' ? 'text-ink-muted' : 'text-ink')}>{t(`journey.${s}`)}</span>
                <span className="block text-caption text-ink-muted">{t(`journey.${s}.hint`)}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

/** What the report will show — an example, labelled as one. */
export const SamplePreview = () => {
  const t = useT();
  return (
    <section aria-labelledby="sample" className="rounded-xl bg-white p-6 shadow-1">
      <p className="eyebrow text-coral-600">{t('l2.trust0')}</p>
      <h2 id="sample" className="mt-1 text-h3">{t('home.sample.title')}</h2>
      <div className="mt-4 rounded-lg bg-paper p-4">
        <p className="eyebrow text-ink-muted">{t('l2.float.title')}</p>
        <div className="mt-2 divide-y divide-line text-label">
          <p className="flex items-center justify-between gap-2 py-2">{t('l2.areaA')}<BandPill band="ok" /></p>
          <p className="flex items-center justify-between gap-2 py-2">{t('l2.areaB')}<BandPill band="watch" /></p>
        </div>
        <p className="mt-1 text-right text-[11px] font-bold text-ink-muted">{t('l2.example')}</p>
      </div>
      <ul className="mt-4 grid gap-2 text-body text-ink-soft">
        {(['l2.peek.point1', 'l2.peek.point2', 'l2.peek.point3'] as const).map((k) => (
          <li key={k} className="flex items-start gap-2">
            <Check className="mt-0.5 h-5 w-5 shrink-0 text-lagoon-600" />
            {t(k)}
          </li>
        ))}
      </ul>
    </section>
  );
};

/** Three things that make the home assessment go well. */
export const BeforeYouStart = () => {
  const t = useT();
  return (
    <section aria-labelledby="before" className="rounded-xl bg-lavender-50 p-6 ring-1 ring-lavender-200">
      <h2 id="before" className="text-h3">{t('home.before.title')}</h2>
      <ol className="mt-4 grid gap-4">
        {([1, 2, 3] as const).map((n) => (
          <li key={n} className="grid grid-cols-[32px_1fr] gap-3">
            <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-white font-display text-[15px] font-extrabold text-sage-600">
              {n}
            </span>
            <span>
              <span className="block text-label text-ink">{t(`home.before.${n}`)}</span>
              <span className="block text-body text-ink-soft">{t(`home.before.${n}.body`)}</span>
            </span>
          </li>
        ))}
      </ol>
      <p className="mt-4 flex items-center gap-2 text-caption text-ink-muted">
        <Lock className="h-4 w-4" />
        {t('home.before.private')}
      </p>
    </section>
  );
};

const ageInMonths = (c?: Child) => (c ? c.ageYears * 12 + c.ageMonths : undefined);

/** Learn pages for this child: their conditions first, then their age. */
export const LearnPicks = ({ child }: { child?: Child }) => {
  const t = useT();
  const conditions = (child?.about?.diagnosis?.conditions ?? []) as ConditionKey[];
  const picks = learnPicks({ ageMonths: ageInMonths(child), conditions });
  return (
    <section aria-labelledby="learn-picks">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="learn-picks" className="text-h3">{child ? t('home.learn.for', { child: child.name }) : t('home.learn.title')}</h2>
        <Link to="/learn" className="text-label font-extrabold text-lagoon-600 underline-offset-4 hover:underline">
          {t('l3.learn.all')} →
        </Link>
      </div>
      <ul className="mt-3 grid gap-4 sm:grid-cols-3">
        {picks.map((a) => (
          <li key={a.slug}>
            <Link to={`/learn/${a.slug}`} className="lift group flex h-full flex-col overflow-hidden rounded-lg bg-white shadow-1">
              <div className="h-[120px] shrink-0">
                <Illustration src={photo(a.photo)} alt="" position="50% 40%" />
              </div>
              <span className="flex flex-1 flex-col gap-1 p-4">
                <span className="text-label text-ink group-hover:text-lagoon-700">{a.title}</span>
                <span className="line-clamp-2 text-caption text-ink-soft">{a.summary}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

/** ZenQuip is a mental-health company: the parent matters too. */
export const ParentCare = () => {
  const t = useT();
  return (
    <section aria-labelledby="parent-care" className="grid overflow-hidden rounded-xl bg-coral-100 sm:grid-cols-[1fr_160px]">
      <div className="p-6">
        <h2 className="flex items-center gap-2 text-h3" id="parent-care">
          <Heart className="h-5 w-5 text-coral-600" />
          {t('home.care.title')}
        </h2>
        <p className="mt-2 text-body text-ink">{t('home.care.body')}</p>
        <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-label">
          <a href="tel:14416" className="font-extrabold text-coral-ink hover:underline">
            Tele-MANAS 14416
          </a>
          <Link to="/learn/caring-for-yourself" className="font-extrabold text-coral-ink hover:underline">
            {t('home.care.read')} →
          </Link>
        </p>
      </div>
      <div aria-hidden="true" className="hidden sm:block">
        <Illustration src={photo('home-parent-care')} alt="" position="50% 40%" />
      </div>
    </section>
  );
};

/** Help that isn't a form: FAQ, trust, a person. */
export const HelpStrip = () => {
  const t = useT();
  return (
    <p className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg bg-white/70 px-5 py-4 text-label ring-1 ring-line">
      <span className="text-ink">{t('home.help.title')}</span>
      <Link to="/faq" className="text-lagoon-700 hover:underline">
        {t('landing.faq')}
      </Link>
      <Link to="/trust" className="text-lagoon-700 hover:underline">
        {t('footer.trust')}
      </Link>
      <a href={`mailto:${COMPANY.supportEmail}`} className="text-lagoon-700 hover:underline">
        {COMPANY.supportEmail}
      </a>
    </p>
  );
};

/** "About Aarav": what the parent told us, or an invitation to tell us. */
export const AboutChildCard = ({ child }: { child: Child }) => {
  const t = useT();
  const a = child.about;
  const facts = [
    a?.diagnosis?.status && a.diagnosis.status !== 'none'
      ? a.diagnosis.conditions.length
        ? `${t(`about.status.${a.diagnosis.status}`)}: ${a.diagnosis.conditions.map((c) => t(`about.condition.${c}`)).join(', ')}`
        : t(`about.status.${a.diagnosis.status}`)
      : undefined,
    a?.therapies?.length ? `${t('about.therapies')}: ${a.therapies.map((th) => t(`about.therapy.${th}`)).join(', ')}` : undefined,
    a?.school?.setting ? t(`about.schoolSetting.${a.school.setting}`) + (a.school.grade ? ` · ${a.school.grade}` : '') : undefined,
    a?.loves ? `${t('home.about.loves')}: ${a.loves}` : undefined,
  ].filter(Boolean) as string[];

  return (
    <section aria-labelledby="about-child" className="rounded-lg bg-white p-5 shadow-1">
      <div className="flex items-baseline justify-between gap-2">
        <h2 id="about-child" className="text-[20px]">{t('home.about.title', { child: child.name })}</h2>
        <Link to={`/children/${child.childId}`} className="inline-flex items-center gap-1 text-caption font-extrabold text-lagoon-600 hover:underline">
          <Pencil className="h-4 w-4" />
          {facts.length ? t('home.about.edit') : t('home.about.add')}
        </Link>
      </div>
      {facts.length ? (
        <ul className="mt-2 grid gap-1.5 text-caption text-ink-soft">
          {facts.map((f) => (
            <li key={f} className="line-clamp-2">
              {f}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-caption text-ink-soft">{t('home.about.empty', { child: child.name })}</p>
      )}
      <Link to={`/children/${child.childId}`} className="mt-3 inline-flex items-center gap-2 text-caption font-extrabold text-lagoon-600 hover:underline">
        <Users className="h-4 w-4" />
        {t('home.about.careTeam')}
      </Link>
    </section>
  );
};
